import { Paper, Stack } from "@mui/material";
import { Button } from "components/ButtonNew";
import { DropdownSingleSelect } from "components/DropdownSingleSelect";
import { IconButton } from "components/IconButton";
import { LabelLessInput } from "components/Input";
import React from "react";
import { cn } from "utils/styling";

import type { BuildingLayerItem } from "../../model/types";
import type { AddNewChildRowProps, LayerCardProps } from "./types";

export const AddNewChildRow = ({
  addDisabled,
  displayOptions = [],
  dropdownValue = "",
  inputPlaceholder,
  inputValue = "",
  label,
  onAdd,
  onDropdownChange,
  onInputChange,
  options = [],
  showInput,
  showOptions,
}: AddNewChildRowProps) => {
  return (
    <Stack className="w-full justify-center gap-4 p-2 px-6">
      {showOptions && options.length > 0 && onDropdownChange && (
        <DropdownSingleSelect
          className="h-10 flex-1"
          clearable={false}
          displayOptions={displayOptions}
          hideLabel
          options={options}
          selectedValue={dropdownValue}
          setSelectedValue={onDropdownChange}
        />
      )}
      {showInput && onInputChange && (
        <LabelLessInput
          height="4rem"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onInputChange(e.target.value)}
          placeholder={inputPlaceholder}
          value={inputValue}
          width="20rem"
        />
      )}
      <Button disabled={addDisabled} onClick={onAdd} size="md" variant="secondary">
        {label}
      </Button>
    </Stack>
  );
};

const DeleteIconButton = ({ onDelete }: { onDelete: () => void }) => (
  <IconButton
    buttonProps={{
      onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onDelete();
      },
      variant: "tertiary",
    }}
    iconProps={{ iconName: "Trash", size: "sm" }}
  />
);

const EditableEntityTitle = ({
  displayTitle,
  editingId,
  entityId,
  name,
  onNameChange,
  setEditingId,
  titleEditable,
}: {
  displayTitle: string;
  editingId: null | string;
  entityId: string;
  name: string;
  onNameChange: (value: string) => void;
  setEditingId: (id: null | string) => void;
  titleEditable: boolean;
}) => {
  const isEditing = titleEditable && editingId === entityId;

  if (isEditing) {
    return (
      <LabelLessInput
        autoFocus
        className="flex-1"
        height="4rem"
        onBlur={() => setEditingId(null)}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          onNameChange(e.target.value);
        }}
        value={name}
      />
    );
  }

  const titleProps = {
    className: cn("cursor-auto truncate p-2 font-bold font-ds-body-semibold-sm "),
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
      if (!titleEditable) return;
      event.stopPropagation();
      setEditingId(entityId);
    },
  };
  if (!titleEditable) return <span {...titleProps}>{displayTitle}</span>;
  return (
    <Button {...titleProps} variant="tertiary">
      {displayTitle}
    </Button>
  );
};

const LayerVisibilityButton = ({
  layer,
  updateLayer,
}: {
  layer: BuildingLayerItem;
  updateLayer: (id: string, updater: (layer: BuildingLayerItem) => BuildingLayerItem) => void;
}) => {
  return (
    <IconButton
      buttonProps={{
        onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          updateLayer(layer.id, (next) => ({ ...next, visible: !next.visible }));
        },
        variant: "tertiary",
      }}
      iconProps={{ iconName: layer.visible ? "EyeOpen" : "EyeClose", size: "sm", svgClassName: "w-4 h-4" }}
    />
  );
};

const cardClassName = (isActive: boolean) => ({
  container: cn(
    " w-full cursor-pointer overflow-hidden rounded-md border border-solid shadow-elevation100 border-ds-subtle",
    isActive && "border-2 border-lds-border-accent"
  ),
  header: cn("bg-core-grey-50"),
});

export const LayerCard = ({
  children,
  className,
  editingId,
  entityId,
  footer,
  isActive,
  layer,
  name,
  onDelete,
  onNameChange,
  onSelect,
  setEditingId,
  title,
  titleEditable,
  updateLayer,
}: LayerCardProps) => {
  const { container, header: headerClass } = cardClassName(isActive);

  return (
    <div className={cn(container, className)} onClick={onSelect}>
      <div className={headerClass}>
        <Stack className="w-full flex-row items-center justify-between gap-2 p-2 text-left">
          <Stack className="flex-1 flex-row items-center gap-1">
            {layer && updateLayer ? <LayerVisibilityButton layer={layer} updateLayer={updateLayer} /> : undefined}
            <Stack className="flex-1">
              <EditableEntityTitle
                displayTitle={title}
                editingId={editingId}
                entityId={entityId}
                name={name}
                onNameChange={onNameChange}
                setEditingId={setEditingId}
                titleEditable={titleEditable}
              />
            </Stack>
          </Stack>
          <DeleteIconButton onDelete={onDelete} />
        </Stack>
        {footer}
      </div>
      <Paper className={cn("rounded-none border-0 bg-lds-surface-bright p-1 shadow-none")}>{children}</Paper>
    </div>
  );
};

export const ObjectRow = ({
  displayLabel,
  editingObjectId,
  isActive,
  objectId,
  onDelete,
  onSelect,
  onUpdateName,
  rawName,
  setEditingId,
}: {
  displayLabel: string;
  editingObjectId: null | string;
  isActive: boolean;
  objectId: string;
  onDelete: () => void;
  onSelect: () => void;
  onUpdateName: (name: string) => void;
  rawName: string;
  setEditingId: (id: null | string) => void;
}) => {
  const isEditing = editingObjectId === objectId;

  return (
    <Stack
      className={cn(
        " w-full cursor-pointer justify-between rounded border border-solid px-2 py-1.5 hover:bg-lds-selection-strong",
        isActive ? "border-lds-border-accent bg-lds-selection-subtle" : "bg-lds-surface-bright border-ds-subtle"
      )}
      onClick={(e) => {
        e.stopPropagation();
        if (!isEditing) onSelect();
      }}
    >
      <Stack className=" flex-1">
        <EditableEntityTitle
          displayTitle={displayLabel}
          editingId={editingObjectId}
          entityId={objectId}
          name={rawName}
          onNameChange={onUpdateName}
          setEditingId={setEditingId}
          titleEditable={true}
        />
      </Stack>
      <DeleteIconButton onDelete={onDelete} />
    </Stack>
  );
};
